import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, updateDoc, deleteDoc, onSnapshot, arrayUnion, arrayRemove, getDoc, increment } from 'firebase/firestore';
import { triggerNewMessageNotification, triggerNewQuestionNotification } from './notificationTriggerService';

/**
 * Send a new message and trigger notification
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} - The created message with ID
 */
export const sendNotificationMessage = async (messageData) => {
  try {
    const message = {
      sender: messageData.sender,
      recipient: messageData.recipient,
      subject: messageData.subject,
      content: messageData.content,
      accountId: messageData.accountId,
      timestamp: serverTimestamp(),
      read: false,
      priority: messageData.priority || 'normal'
    };

    // Add message to Firestore
    const messageRef = await addDoc(collection(db, 'messages'), message);
    
    console.log('✅ Message sent successfully:', messageRef.id);

    // Trigger new message notification (participants only — pass recipientId for direct messages)
    await triggerNewMessageNotification({
      sender: messageData.sender,
      senderId: messageData.senderId,
      subject: messageData.subject,
      content: messageData.content,
      accountId: messageData.accountId,
      participants: messageData.recipientId ? [messageData.recipientId] : [],
      timestamp: new Date().toISOString()
    });

      return {
      id: messageRef.id,
      ...message,
      timestamp: new Date()
      };
  } catch (error) {
    console.error('❌ Error sending message:', error);
    throw error;
  }
};

/**
 * Create a new question and trigger notification
 * @param {Object} questionData - The question data
 * @returns {Promise<Object>} - The created question with ID
 */
export const createQuestion = async (questionData) => {
  try {
    const question = {
      questioner: questionData.questioner,
      question: questionData.question,
      category: questionData.category,
      priority: questionData.priority || 'medium',
      status: 'open',
      timestamp: serverTimestamp(),
      assignedTo: questionData.assignedTo || null
    };

    // Add question to Firestore
    const questionRef = await addDoc(collection(db, 'questions'), question);
    
    console.log('✅ Question created successfully:', questionRef.id);

    // Trigger new question notification
    await triggerNewQuestionNotification({
      questioner: questionData.questioner,
      question: questionData.question,
      category: questionData.category,
      priority: questionData.priority || 'medium',
      timestamp: new Date().toISOString()
    });

    return {
      id: questionRef.id,
      ...question,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('❌ Error creating question:', error);
    throw error;
  }
};

// ===== EXISTING FUNCTIONS FOR COMPATIBILITY =====

/**
 * Get conversations for a specific user
 */
export const getUserSpecificConversations = async (userId) => {
  try {
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const conversations = [];
    
    querySnapshot.forEach((doc) => {
      conversations.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by lastMessageAt on the client side
    conversations.sort((a, b) => {
      const aTime = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(0);
      const bTime = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(0);
      return bTime - aTime;
    });
    
    return conversations;
  } catch (error) {
    console.error('Error fetching user conversations:', error);
    throw error;
  }
};

/**
 * Get total unread count for a user
 */
export const getUserTotalUnreadCount = async (userId) => {
  try {
    console.log('getUserTotalUnreadCount called for user:', userId);
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId)
    );
    
    const querySnapshot = await getDocs(q);
    let totalUnread = 0;
    
    console.log('Found conversations for user:', querySnapshot.size);
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('Conversation data:', {
        id: doc.id,
        participants: data.participants,
        unreadCount: data.unreadCount,
        userUnreadCount: data.unreadCount?.[userId]
      });
      
      if (data.unreadCount && data.unreadCount[userId]) {
        totalUnread += data.unreadCount[userId];
      }
    });
    
    console.log('Total unread messages for user:', totalUnread);
    return totalUnread;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};

/**
 * Subscribe to a user's total unread message count across all conversations.
 * Keeps dashboard badges live so they clear the moment a thread is read.
 * @returns {Function} unsubscribe
 */
export const subscribeToTotalUnreadCount = (userId, callback) => {
  if (!userId) return () => {};
  const conversationsRef = collection(db, 'conversations');
  const q = query(conversationsRef, where('participants', 'array-contains', userId));

  return onSnapshot(
    q,
    (querySnapshot) => {
      let totalUnread = 0;
      querySnapshot.forEach((docSnap) => {
        const count = docSnap.data()?.unreadCount?.[userId];
        if (typeof count === 'number' && count > 0) {
          totalUnread += count;
        }
      });
      callback(totalUnread);
    },
    (error) => {
      console.error('subscribeToTotalUnreadCount error:', error);
      callback(0);
    }
  );
};

/**
 * Create a new conversation
 */
export const createConversation = async (conversationData) => {
  try {
    const conversation = {
      title: conversationData.title,
      participants: conversationData.participants,
      createdBy: conversationData.createdBy,
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      unreadCount: {},
      isGroup: conversationData.isGroup || false
    };

    // Initialize unread count for each participant
    conversationData.participants.forEach(participantId => {
      conversation.unreadCount[participantId] = 0;
    });

    const conversationRef = await addDoc(collection(db, 'conversations'), conversation);
    
    return conversationRef.id;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Create a group conversation
 */
export const createGroupConversation = async (conversationData) => {
  return await createConversation({
    ...conversationData,
    isGroup: true
  });
};

/**
 * Subscribe to conversations for a user
 */
export const subscribeToConversations = (userId, callback) => {
  const conversationsRef = collection(db, 'conversations');
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const conversations = [];
    querySnapshot.forEach((doc) => {
      conversations.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by lastMessageAt on the client side
    conversations.sort((a, b) => {
      const aTime = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(0);
      const bTime = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(0);
      return bTime - aTime;
    });
    
    callback(conversations);
  });
};

/** Milliseconds for sorting; missing timestamps sort first so nothing is dropped from the query. */
const messageSortMillis = (m) => {
  const t = m?.timestamp;
  if (t?.toMillis) return t.toMillis();
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'number' && !Number.isNaN(t)) return t;
  return 0;
};

const sortConversationMessages = (messages) =>
  [...messages].sort(
    (a, b) => messageSortMillis(a) - messageSortMillis(b) || String(a.id || '').localeCompare(String(b.id || ''))
  );

/**
 * Get messages for a specific conversation
 */
export const getUserConversationMessages = async (conversationId) => {
  try {
    const messagesRef = collection(db, 'messages');
    // Equality-only query so documents without `timestamp` are still returned (orderBy would exclude them).
    const q = query(messagesRef, where('conversationId', '==', conversationId));

    const querySnapshot = await getDocs(q);
    const messages = [];

    querySnapshot.forEach((docSnap) => {
      messages.push({
        id: docSnap.id,
        ...docSnap.data()
      });
    });

    return sortConversationMessages(messages);
  } catch (error) {
    console.error('Error fetching conversation messages:', error);
    throw error;
  }
};

/**
 * Mark conversation as read for a user
 */
export const markConversationAsRead = async (conversationId, userId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`unreadCount.${userId}`]: 0,
      // Drives read receipts: a message is "read" by this user once their lastReadAt passes the message timestamp
      [`lastReadAt.${userId}`]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

/**
 * Subscribe to messages for a conversation
 */
export const subscribeToUserMessages = (conversationId, callback, onError) => {
  const messagesRef = collection(db, 'messages');
  const q = query(messagesRef, where('conversationId', '==', conversationId));

  return onSnapshot(
    q,
    (querySnapshot) => {
      const messages = [];
      querySnapshot.forEach((docSnap) => {
        messages.push({ id: docSnap.id, ...docSnap.data() });
      });
      callback(sortConversationMessages(messages));
    },
    (error) => {
      console.error('subscribeToUserMessages snapshot error:', conversationId, error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  );
};

/**
 * Update conversation title
 */
export const updateConversationTitle = async (conversationId, newTitle) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      title: newTitle
    });
  } catch (error) {
    console.error('Error updating conversation title:', error);
    throw error;
  }
};

/**
 * Add participant to conversation
 */
export const addParticipantToConversation = async (conversationId, participantId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      participants: arrayUnion(participantId),
      [`unreadCount.${participantId}`]: 0
    });
  } catch (error) {
    console.error('Error adding participant to conversation:', error);
    throw error;
  }
};

/**
 * Send a message in a conversation
 */
export const sendMessage = async (conversationId, senderId, content, attachments = []) => {
  try {
    const message = {
      conversationId: conversationId,
      senderId: senderId,
      content: content,
      attachments: attachments,
      timestamp: serverTimestamp(),
      read: false
    };

    // Add message to Firestore
    const messageRef = await addDoc(collection(db, 'messages'), message);
    
    // Update conversation's last message time and increment unread count for other participants
    const conversationRef = doc(db, 'conversations', conversationId);
    
    // Get conversation data to find participants
    const conversationDoc = await getDoc(conversationRef);
    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const participants = conversationData.participants || [];
      
      // Atomic increments avoid lost updates when several messages land at once,
      // which previously left unread counts stuck above zero.
      const unreadCountUpdates = {};
      participants.forEach(participantId => {
        if (participantId !== senderId) {
          unreadCountUpdates[`unreadCount.${participantId}`] = increment(1);
        }
      });

      // Update conversation with last message info and unread counts
      await updateDoc(conversationRef, {
        lastMessageAt: serverTimestamp(),
        lastMessage: content,
        lastMessageSenderId: senderId,
        ...unreadCountUpdates
      });
      
      console.log('✅ Updated unread counts for participants:', unreadCountUpdates);
    } else {
      // Fallback: just update last message info
      await updateDoc(conversationRef, {
        lastMessageAt: serverTimestamp(),
        lastMessage: content
      });
    }
    
    console.log('✅ Message sent successfully:', messageRef.id);
    
    // Trigger new message notification
    try {
      // Get conversation details to find participants
      const conversationDoc = await getDoc(conversationRef);
      if (conversationDoc.exists()) {
        const conversationData = conversationDoc.data();
        const participants = conversationData.participants || [];
        
        console.log('🔍 Conversation data:', conversationData);
        console.log('👥 Participants:', participants);
        console.log('📤 Sender ID:', senderId);
        
        // Get sender name
        let senderName = 'Unknown User';
        try {
          console.log('🔍 Looking up sender ID:', senderId);
          
          // First try to get from internal_users collection
          const senderDoc = await getDoc(doc(db, 'internal_users', senderId));
          if (senderDoc.exists()) {
            const senderData = senderDoc.data();
            console.log('📋 Sender data from internal_users:', senderData);
            
            const fullName = `${senderData.firstName || ''} ${senderData.lastName || ''}`.trim();
            console.log('🔍 Name parts - firstName:', senderData.firstName, 'lastName:', senderData.lastName, 'fullName:', fullName);
            console.log('🔍 Other name fields - displayName:', senderData.displayName, 'name:', senderData.name);
            
            // Try multiple name combinations
            if (fullName && fullName !== ' ') {
              senderName = fullName;
            } else if (senderData.displayName) {
              senderName = senderData.displayName;
            } else if (senderData.name) {
              senderName = senderData.name;
            } else if (senderData.email) {
              // Extract name from email if no other name is available
              const emailName = senderData.email.split('@')[0];
              senderName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            } else {
              senderName = 'Unknown User';
            }
            console.log('✅ Sender name resolved from internal_users:', senderName);
          } else {
            console.log('❌ Sender document not found in internal_users for ID:', senderId);
            
            // Try to get from auth user if available
            const { auth } = await import('../firebase');
            if (auth.currentUser && auth.currentUser.uid === senderId) {
              senderName = auth.currentUser.displayName || auth.currentUser.email || 'Current User';
              console.log('✅ Sender name from auth:', senderName);
            } else {
              // Try to get from users collection as fallback
              const userDoc = await getDoc(doc(db, 'users', senderId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                console.log('🔍 Name parts from users - firstName:', userData.firstName, 'lastName:', userData.lastName, 'fullName:', fullName);
                console.log('🔍 Other name fields from users - displayName:', userData.displayName, 'name:', userData.name);
                
                // Try multiple name combinations
                if (fullName && fullName !== ' ') {
                  senderName = fullName;
                } else if (userData.displayName) {
                  senderName = userData.displayName;
                } else if (userData.name) {
                  senderName = userData.name;
                } else if (userData.email) {
                  // Extract name from email if no other name is available
                  const emailName = userData.email.split('@')[0];
                  senderName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
                } else {
                  senderName = 'Unknown User';
                }
                console.log('✅ Sender name resolved from users:', senderName);
              } else {
                console.log('❌ Sender document not found in users collection either');
                // Last resort - use the sender ID as the name
                senderName = `User ${senderId.substring(0, 8)}`;
                console.log('⚠️ Using fallback sender name:', senderName);
              }
            }
          }
        } catch (error) {
          console.error('❌ Error fetching sender name:', error);
          senderName = 'Unknown User';
        }
        
        // Filter out the sender from participants - they shouldn't receive a notification for their own message
        const recipients = participants.filter(participantId => participantId !== senderId);
        
        await triggerNewMessageNotification({
          sender: senderName,
          senderId: senderId, // Include senderId so notification service can filter it out
          subject: conversationData.title || 'New Message',
          content: content,
          conversationId: conversationId,
          participants: recipients,
          timestamp: new Date().toISOString()
        });
        console.log('✅ New message notification sent successfully');
      }
    } catch (notificationError) {
      console.error('❌ Error sending new message notification:', notificationError);
      // Don't fail the message sending if notification fails
    }
    
    return messageRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Get conversations for a user (alias for getUserSpecificConversations)
 */
export const getUserConversations = getUserSpecificConversations;

/**
 * Get a specific conversation
 */
export const getConversation = async (conversationId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      return {
        id: conversationDoc.id,
        ...conversationDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

/**
 * Get messages for a conversation (alias for getUserConversationMessages)
 */
export const getConversationMessages = getUserConversationMessages;

/**
 * Delete a message
 */
export const deleteMessage = async (messageId) => {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await deleteDoc(messageRef);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Subscribe to messages for a conversation (alias for subscribeToUserMessages)
 */
export const subscribeToMessages = subscribeToUserMessages;

/**
 * Search conversations
 */
export const searchConversations = async (userId, searchTerm) => {
  try {
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const conversations = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.title && data.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        conversations.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Sort by lastMessageAt on the client side
    conversations.sort((a, b) => {
      const aTime = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(0);
      const bTime = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(0);
      return bTime - aTime;
    });
    
    return conversations;
  } catch (error) {
    console.error('Error searching conversations:', error);
    throw error;
  }
};

/**
 * Delete conversation for a user
 */
export const deleteConversationForUser = async (conversationId, userId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      participants: arrayRemove(userId)
    });
  } catch (error) {
    console.error('Error deleting conversation for user:', error);
    throw error;
  }
};